/**
 * Drawing Facade (100% Shared)
 * 
 * Wraps all drawing-related state and actions.
 * Handles drawing creation, cancellation, and state management.
 * 
 * This facade is completely platform-agnostic and can be used
 * by both web and plugin without any modifications.
 * 
 * Usage:
 * ```tsx
 * const drawing = useDrawingFacade(projectId);
 * await drawing.createDrawing(conversationId, 'journey_map');
 * ```
 * 
 * @module useDrawingFacade
 * @category Facades/Shared
 */

import { useCallback, useRef, useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { drawingService } from '../../../api/services';
import { getUserProfile } from '../../../store/slices/appSlice';
import { useAccessToken } from '../../useAccessToken';
import type { QuestionnaireType, ConversationHeaders, Message } from '../../../types';
import type { DrawingCreationResponse } from '../../../types/drawing.types';

export interface DrawingFacade {
  // ========== State ==========
  /**
   * Whether a drawing is currently being created
   */
  isCreatingDrawing: boolean;

  /**
   * Drawing creation error message
   */
  drawingError: string | null;

  /**
   * Type of drawing that can be created from conversation
   */
  canBeDrawn: QuestionnaireType | null;

  /**
   * Type of drawing ready to generate
   */
  readyToGenerate: string | null;

  /**
   * Whether waiting for drawing creation to complete
   */
  isWaitingForDrawingCreation: boolean;

  /**
   * Whether to show patient banner during creation
   */
  showBannerPatient: boolean;

  /**
   * Whether drawing needs more time
   */
  isNeedTime: boolean;

  // ========== Actions ==========
  /**
   * Create a drawing from a conversation
   * Returns the drawing data for plugin communication
   */
  createDrawing: (conversationId: string, type?: QuestionnaireType) => Promise<DrawingCreationResponse | undefined>;

  /**
   * Cancel ongoing drawing creation
   */
  cancelDrawing: () => void;

  /**
   * Set the type of drawing that can be created
   */
  setCanBeDrawn: (type: QuestionnaireType | null) => void;

  /**
   * Set the type of drawing ready to generate
   */
  setReadyToGenerate: (type: string | null) => void;

  /**
   * Set drawing creation state
   */
  setIsCreatingDrawing: (value: boolean) => void;

  /**
   * Set waiting for drawing creation state
   */
  setIsWaitingForDrawingCreation: (value: boolean) => void;

  /**
   * Set show patient banner state
   */
  setShowBannerPatient: (value: boolean) => void;

  /**
   * Set needs time state
   */
  setIsNeedTime: (value: boolean) => void;

  /**
   * Process drawing-related headers from conversation
   */
  processDrawingHeaders: (headers: ConversationHeaders) => void;

  /**
   * Handle drawing creation with UI state management
   */
  handleDrawingCreation: (
    conversationId: string,
    type: QuestionnaireType,
    overrideProjectId?: string | null
  ) => Promise<void>;

  /**
   * Clear any drawing errors
   */
  clearError: () => void;
}

/**
 * Parameters for drawing facade message handling (optional, for plugin mode)
 */
export interface DrawingMessageHandlers {
  /**
   * Messages array to get the last message ID
   */
  messages: Message[];
  
  /**
   * Current conversation ID
   */
  conversationId: string | null;
  
  /**
   * Callback to update banner on a message
   */
  onUpdateBanner: (messageId: string, bannerType: 'success' | 'error', drawType: string) => void;
  
  /**
   * Callback to send hidden message to conversation
   */
  onSendHiddenMessage: (message: string, conversationId: string, actionType: string) => void;
  
  /**
   * Whether we're in web mode (if true, message listening is disabled)
   */
  isWeb?: boolean;
  
  /**
   * Function to detect if message content is RTL
   */
  detectRtl?: (text: string) => boolean;
}

/**
 * Drawing Facade Hook
 * 
 * Provides drawing management capabilities.
 * 100% shared between web and plugin.
 * 
 * @param projectId - Optional project ID for project-scoped drawings
 * @param messageHandlers - Optional message handlers for plugin message listening (plugin mode only)
 */
export function useDrawingFacade(
  projectId?: string | null,
  messageHandlers?: DrawingMessageHandlers
): DrawingFacade {
  // ========== Redux State ==========
  const dispatch = useAppDispatch();
  const accessToken = useAccessToken();

  // ========== State ==========
  const [isCreatingDrawing, setIsCreatingDrawing] = useState(false);
  const [drawingError, setDrawingError] = useState<string | null>(null);
  const [canBeDrawn, setCanBeDrawn] = useState<QuestionnaireType | null>(null);
  const [readyToGenerate, setReadyToGenerate] = useState<string | null>(null);
  const [isWaitingForDrawingCreation, setIsWaitingForDrawingCreation] = useState(false);
  const [showBannerPatient, setShowBannerPatient] = useState(false);
  const [isNeedTime, setIsNeedTime] = useState(false);

  // ========== Refs ==========
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Create a drawing from a conversation
   * Returns the drawing data for plugin communication
   */
  const createDrawing = useCallback(
    async (conversationId: string, type?: QuestionnaireType) => {
      if (!conversationId) {
        console.error('No conversation ID provided for drawing creation');
        setDrawingError('No conversation ID available');
        return;
      }

      // Cancel any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      try {
        setIsCreatingDrawing(true);
        setDrawingError(null);

        const response = await drawingService.createDrawing({
          conversationId,
          projectId,
          type,
        });

        console.log('Drawing created successfully:', response);
        
        // Return the drawing data for plugin communication
        return response;
      } catch (error) {
        console.error('Error creating drawing:', error);

        let errorMessage = 'Failed to create drawing';
        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            errorMessage = 'Drawing creation cancelled';
          } else {
            errorMessage = error.message || errorMessage;
          }
        }

        setDrawingError(errorMessage);
        throw error;
      } finally {
        setIsCreatingDrawing(false);

        // Silent refresh: Update user data (drawing credits may have changed)
        // Fire-and-forget, silently fail if error
        if (accessToken) {
          dispatch(getUserProfile(accessToken)).catch(() => {
            // Silent failure - don't interrupt user flow
          });
        }
      }
    },
    [projectId]
  );

  /**
   * Cancel ongoing drawing creation
   */
  const cancelDrawing = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsCreatingDrawing(false);
    setCanBeDrawn(null);
    setReadyToGenerate(null);
    setShowBannerPatient(false);
    setIsNeedTime(false);
    setDrawingError(null);
  }, []);

  /**
   * Process drawing-related headers from conversation response
   */
  const processDrawingHeaders = useCallback((headers: ConversationHeaders) => {
    if (headers.canBeDrawn) {
      setCanBeDrawn(headers.canBeDrawn);
      setIsWaitingForDrawingCreation(true);
    }

    if (headers.readyToGenerate) {
      setReadyToGenerate(headers.readyToGenerate);
    }
  }, []);

  /**
   * Handle drawing creation with full UI state management
   * In plugin mode, also posts messages to the Figma plugin
   */
  const handleDrawingCreation = useCallback(
    async (
      conversationId: string,
      type: QuestionnaireType,
      overrideProjectId?: string | null
    ) => {
      setShowBannerPatient(true);
      setIsCreatingDrawing(true);
      setIsNeedTime(false);
      setDrawingError(null);

      // Cancel any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      try {
        // Create drawing via API service directly to have full control over state
        // This matches the old implementation behavior (useDrawingIntegration.ts)
        const drawingData = await drawingService.createDrawing({
          conversationId,
          projectId: overrideProjectId || projectId,
          type,
        });
        
        console.log('Drawing created successfully:', drawingData);
        
        // In plugin environment, post message to Figma plugin
        if (typeof parent !== 'undefined' && parent.postMessage) {
          // Map drawing types to plugin message types
          // Matches the mapping in old useDrawingIntegration.ts
          const messageTypeMap: Record<string, string> = {
            'persona': 'createPersona',
            'problem_statement': 'createProblemStatements',
            'interview_question': 'createInterviewQuestions',
            'survey_question': 'createSurveyQuestions',
            'benchmark': 'createBenchmarkReport',
            'how_might_we': 'createHowMightWe',
            'five_whys': 'createFiveWhys',
            'journey_map': 'createJourneyMap',
            'user_flow': 'createUserFlowChart',
            'mind_map': 'createMindMap',
            'user_story': 'createUserStories',
          };

          const messageType = messageTypeMap[type];
          if (messageType) {
            parent.postMessage({
              pluginMessage: { type: messageType, data: drawingData['response'] || drawingData }
            }, '*');
          }
        }

        setShowBannerPatient(false);
        setIsWaitingForDrawingCreation(false);
      } catch (error) {
        console.error('Drawing creation failed:', error);
        setIsNeedTime(true);
        
        let errorMessage = 'Failed to create drawing';
        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            errorMessage = 'Drawing creation cancelled';
          } else {
            errorMessage = error.message || errorMessage;
          }
        }
        setDrawingError(errorMessage);
        throw error;
      } finally {
        setIsCreatingDrawing(false);
      }
    },
    [projectId]
  );

  /**
   * Clear any drawing errors
   */
  const clearError = useCallback(() => {
    setDrawingError(null);
  }, []);

  /**
   * Listen for plugin messages after drawing completion
   * Shows success banner and sends hidden message when drawing finishes
   * Matches old implementation behavior (useMessageEvents hook)
   */
  useEffect(() => {
    // Only listen in plugin mode when canBeDrawn is set
    if (!messageHandlers || messageHandlers.isWeb || !canBeDrawn) {
      return;
    }

    const { messages, conversationId, onUpdateBanner, onSendHiddenMessage, detectRtl } = messageHandlers;

    const handleMessage = (event: MessageEvent) => {
      const { type, data } = event.data.pluginMessage || {};
      
      const validTypes = [
        'createWireframe',
        'createPersona', 
        'createProblemStatements',
        'createInterviewQuestions',
        'createSurveyQuestions',
        'createBenchmarkReport',
        'createHowMightWe',
        'createFiveWhys',
        'createJourneyMap',
        'createUserFlowChart',
        'createMindMap',
        'createUserStories',
      ];

      if (!validTypes.includes(type)) return;

      const lastMessage = messages[messages.length - 1];
      if (!lastMessage) return;

      if (data?.status === 'success') {
        // Reset drawing state
        setShowBannerPatient(false);
        setIsNeedTime(false);
        
        // Update banner on last message to show success
        onUpdateBanner(lastMessage.id, 'success', canBeDrawn);
        
        // Send hidden success message to conversation
        if (conversationId) {
          // Get success message based on RTL detection
          const lastMessageContent = lastMessage.content || '';
          const isRtl = detectRtl ? detectRtl(lastMessageContent) : false;
          
          const successMessage = isRtl 
            ? "أعطني وصفًا موجزًا   لما أنتجته للتو في جملة واحدة. ثم اسألني إن كان هذا ما أتوقعه أم لا. "
            : "Give me a brief description of what you just generated in one sentence. Then ask me if that's what I expect or not.";
          
          const action_type = `success_${canBeDrawn.toLowerCase().replace(/\s+/g, '_')}`;
          
          // Send hidden message
          onSendHiddenMessage(successMessage, conversationId, action_type);
        }
        
        // Reset drawing state
        setCanBeDrawn(null);
        setIsCreatingDrawing(false);
      } else if (data?.status === 'error') {
        // Handle error case
        onUpdateBanner(lastMessage.id, 'error', canBeDrawn);
        
        setShowBannerPatient(false);
        setIsNeedTime(false);
        setIsCreatingDrawing(false);
        
        console.error('Error creating drawing:', data);
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Set "need time" banner after 5 seconds (matching old implementation)
    const needTimeId = setTimeout(() => {
      if (canBeDrawn) {
        setIsNeedTime(true);
      }
    }, 5000);
    
    // Set patient banner after 30 seconds (matching old implementation)
    const timeoutId = setTimeout(() => {
      if (canBeDrawn) {
        setShowBannerPatient(true);
        setIsNeedTime(false);
      }
    }, 30000);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timeoutId);
      clearTimeout(needTimeId);
    };
  }, [canBeDrawn, messageHandlers]);

  return {
    // State
    isCreatingDrawing,
    drawingError,
    canBeDrawn,
    readyToGenerate,
    isWaitingForDrawingCreation,
    showBannerPatient,
    isNeedTime,

    // Actions
    createDrawing,
    cancelDrawing,
    setCanBeDrawn,
    setReadyToGenerate,
    setIsCreatingDrawing,
    setIsWaitingForDrawingCreation,
    setShowBannerPatient,
    setIsNeedTime,
    processDrawingHeaders,
    handleDrawingCreation,
    clearError,
  };
}
